/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, TextField } from "@mui/material";

import useStyle from "./styles";
import { MESSAGES, TITLE } from "./constants";
import Loader from "../../shared/loader/Loader";
import FormDialog from "../../shared/dialog/FormDialog";
import { IFormValues } from "../../models/Customers/customer";
import { selectError } from "../../store/error/errorSelector";
import { ErrorMessage } from "../../shared/ErrorMessage/ErrorMessage";
import { CustomTooltip } from "../../shared/customTooltip/CustomTooltip";
import { createCustomer, editCustomer } from "../../store/customer/customer";
import { hideLoaderAction } from "../../store/loader/loaderActionCreator";
import { showErrorAction } from "../../store/error/errorActionCreator";

export interface ICustomerData {
  id: string;
  jpowerId: string;
  name: string;
};

interface IAddCustomerDialogProps {
  isEdit: boolean;
  openModal: boolean;
  refreshOnSave: Function;
  customerData?: ICustomerData;
  openCloseModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const AddCustomerDialog = (props: IAddCustomerDialogProps) => {
  const { openModal, openCloseModal } = props;
  const classes = useStyle();
  const dispatch = useDispatch();

  const formDefaultValues: ICustomerData = {
    id: "",
    jpowerId: "",
    name: "",
  };

  const formErrorDefaultValues = {
    error: false,
    errorHelperText: "",
    globalRealmNameError: false,
    globalRealmNameErrorHelperText: "",
    jpowerIdError: false,
    jpowerIdErrorHelperText: "",
  };

  const errorMesaage = useSelector(selectError);

  const [showLoader, showHideLoader] = useState(false);
  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);
  const [formValues, updateFormValues] = useState<ICustomerData>(formDefaultValues);

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    updateFormError({ ...formErrorDefaultValues });
    dispatch(hideLoaderAction());
    dispatch(showErrorAction({ message: '' }))
  }, [props.openModal]);

  const handleClose = () => {
    openCloseModal(!openModal);
  };

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    showHideLoader(false);
    if (props.isEdit && props.customerData) {
      updateFormValues({ ...props.customerData });
    }
  }, [openModal, props.isEdit, props.customerData]);

  const updateFormFields = (event: { target: { name: string; value: string } }) => {
    let targetValue = event.target.value;
    if (event.target.name === "jpowerId") {
      const parsedInt = parseInt(event.target.value);
      if (parsedInt) {
        targetValue = parsedInt.toString();
      } else {
        targetValue = "";
      }
      updateFormValues({ ...formValues, jpowerId: targetValue });
      updateFormError({ ...formErrorState, jpowerIdError: false });
    } else {
      const key = event.target.name === "globalRealmName" ? event.target.name : "name";
      const errorKey = event.target.name = "error";
      updateFormValues({ ...formValues, [key]: targetValue });
      updateFormError({ ...formErrorState, [errorKey]: false });
    }
  };

  const validateRequired = (values: IFormValues) => {
    if (!values.name) return false;
    else if (!values.jpowerId) return false;
    else return true;
  };
  const handleSubmit = async () => {
    let isSucccess = false;
    if (validateRequired(formValues)) {
      showHideLoader(true);
      if (props.isEdit) {
        isSucccess = await editCustomer(dispatch, formValues);
      } else {
        isSucccess = await createCustomer(dispatch, formValues);
      }
      if (isSucccess) handleClose();
      showHideLoader(false);
    }
  };

  return (
    <Fragment>
      <FormDialog
        dialogAction1={
          <Button
            color="primary"
            disableElevation
            name="cancelBtn"
            onClick={handleClose}
            variant="outlined"
          >
            {TITLE.CANCEL}
          </Button>
        }
        dialogAction2={
          <Button
            color="primary"
            disableElevation
            disabled={showLoader}
            name="createCustomerBtn"
            onClick={handleSubmit}
            variant="contained"
          >
            {TITLE.SAVE}
          </Button>
        }
        dialogTitle={props.isEdit ? TITLE.EDIT_CUSTOMER : TITLE.ADD_CUSTOMER}
        handleClose={handleClose}
        openModal={openModal}
      >
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="name"
            name="displayName"
            variant="standard"
            disabled={showLoader}
            value={formValues.name}
            label={TITLE.CUSTOMER_NAME}
            onChange={updateFormFields}
            error={formErrorState.error}
            className={classes.modalTextBox}
            helperText={formErrorState.errorHelperText}
          />
          <CustomTooltip title={MESSAGES.CUSTOMER_NAME_TEXT}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="jpowerId"
            name="jpowerId"
            variant="standard"
            label={TITLE.JPOWER_ID}
            onChange={updateFormFields}
            value={formValues.jpowerId}
            className={classes.modalTextBox}
            error={formErrorState.jpowerIdError}
            helperText={formErrorState.jpowerIdErrorHelperText}
          />
          <CustomTooltip title={MESSAGES.JPOWER_ID_TEXT}></CustomTooltip>
        </Box>
        {/* {errorMesaage && <ErrorMessage message={errorMesaage.message} />} */}
        <Loader showLoader={showLoader} isCenter />
      </FormDialog>
    </Fragment>
  );
};

export default AddCustomerDialog;
